"use client";
// We are using Pages Router, but Next still complains if we don't include use client here.

import {
  createContext,
  useContext,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { REGIONAL_INSTANCE_CONFIG } from "./regional-instance-constants";
import type {
  RegionalInstanceCapability,
  RegionalInstanceId,
} from "./regional-instance-types";
import { resolveRegionalInstanceId } from "./helper-functions";
import { LoginMethod } from "../../../components/Authentication/LoginMethods/types";

type RegionalInstanceContextType = {
  id: RegionalInstanceId;
};

const RegionalInstanceContext = createContext<
  RegionalInstanceContextType | undefined
>(undefined);

export function useRegionalInstanceContext(): RegionalInstanceContextType {
  const context = useContext(RegionalInstanceContext);
  if (!context) throw new Error("Regional instance context is not available");
  return context;
}

/* -------------------------------------------------------------------------- */
/*  Selectors — derive values from the active regional instance id.           */
/* -------------------------------------------------------------------------- */

export function useRegionalInstanceCapability(
  capability: RegionalInstanceCapability
): boolean {
  const { id } = useRegionalInstanceContext();
  return REGIONAL_INSTANCE_CONFIG[id].capabilities[capability];
}

export function useRegionalInstanceDefaultLoginMethod(): LoginMethod {
  const { id } = useRegionalInstanceContext();
  return REGIONAL_INSTANCE_CONFIG[id].login.default;
}

export function useRegionalInstanceLoginMethods(): LoginMethod[] {
  const { id } = useRegionalInstanceContext();
  return REGIONAL_INSTANCE_CONFIG[id].login.methods;
}

function getBrowserRegionalInstanceId(): RegionalInstanceId {
  return resolveRegionalInstanceId({
    hostname: window.location.hostname,
  });
}

/** SSR snapshot — must match client resolution in dev (e.g. SITE_ORIGIN_DEBUG=cn). */
function getServerRegionalInstanceId(): RegionalInstanceId {
  return resolveRegionalInstanceId({});
}

function subscribe(): () => void {
  return () => {};
}

export function RegionalInstanceProvider({
  children,
}: {
  children: ReactNode;
}) {
  const id = useSyncExternalStore(
    subscribe,
    getBrowserRegionalInstanceId,
    getServerRegionalInstanceId
  );

  return (
    <RegionalInstanceContext.Provider value={{ id }}>
      {children}
    </RegionalInstanceContext.Provider>
  );
}
